import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

export function EnhancedStatCard({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  trendLabel = 'vs last month',
  sparklineData = [],
  gradient = 'from-blue-50 to-white dark:from-blue-900/20 dark:to-background',
  iconColor = 'text-blue-600 dark:text-blue-400',
  iconBg = 'bg-blue-100 dark:bg-blue-900/30',
  delay = 0
}) {
  // Generate simple sparkline data if not provided
  const chartData = sparklineData.length > 0 
    ? sparklineData 
    : Array.from({ length: 7 }, (_, i) => ({
        value: Math.floor(Math.random() * 100) + 50
      }));

  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3" />;
    if (trend === 'down') return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600 dark:text-green-400';
    if (trend === 'down') return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
      <Card className={`relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${gradient}`}>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                {title}
              </p>
              <motion.p
                className="text-3xl font-bold"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: delay + 0.2 }}
              >
                {value}
              </motion.p>
              
              {/* Trend Indicator */}
              {trendValue && (
                <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${getTrendColor()}`}>
                  {getTrendIcon()}
                  <span>{trendValue}</span>
                  <span className="text-muted-foreground ml-1">{trendLabel}</span>
                </div>
              )}
            </div>

            {/* Icon */}
            <motion.div
              className={`p-3 rounded-xl ${iconBg}`}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.2 }}
            >
              <Icon className={`h-6 w-6 ${iconColor}`} />
            </motion.div>
          </div>

          {/* Sparkline Chart */}
          {chartData.length > 0 && (
            <div className="h-12 mt-2 -mb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="currentColor"
                    strokeWidth={2}
                    dot={false}
                    className={iconColor}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>

        {/* Decorative gradient overlay */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/20 to-transparent rounded-full -mr-16 -mt-16 dark:from-white/10" />
      </Card>
    </motion.div>
  );
}
